import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectOrganizationData } from 'pages/redux';
import { selectPermission, selectToken } from 'redux/global';
import { useLogs } from 'pages/Logs/hooks';
import {
  selectActivityLogsSearchHasMore,
  selectActivityLogsSearchLoadedPageCount,
  selectActivityLogsSearchPage,
  selectActivityLogsSearchStatus,
  selectLogs,
  appendActivityLogsSearchLoadedPage,
  setActivityLogsMergedLogs,
  setActivityLogsSearchStatus,
} from 'pages/Logs/redux';
import { ACTIVITY_LOGS_PER_PAGE } from 'pages/Logs/activityLogsTableAmount';
import {
  mergeActivityLogsPages,
  persistSharedActivityLogsPage,
} from 'utils/activityLogsMergedPool';

/** Metrics + analytics share downloaded pages; arrow fetches the next BE page. */
export const useMetricsActivityLogs = () => {
  const dispatch = useAppDispatch();
  const organizationData = useAppSelector(selectOrganizationData);
  const logs = useAppSelector(selectLogs);
  const searchStatus = useAppSelector(selectActivityLogsSearchStatus);
  const hasMore = useAppSelector(selectActivityLogsSearchHasMore);
  const loadedPageCount = useAppSelector(selectActivityLogsSearchLoadedPageCount);
  const currentPage = useAppSelector(selectActivityLogsSearchPage);
  const token = useAppSelector(selectToken);
  const permission = useAppSelector(selectPermission);
  const { runActivityLogsSearch } = useLogs();
  const [isFetchingPage, setIsFetchingPage] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const initialFetchStarted = useRef(false);

  const orgId = organizationData?.id != null ? String(organizationData.id) : '';

  const refreshMergedPool = useCallback(async () => {
    if (!orgId) return;
    setIsMerging(true);
    try {
      const merged = await mergeActivityLogsPages(
        orgId,
        loadedPageCount,
        currentPage,
        logs,
      );
      dispatch(setActivityLogsMergedLogs(merged));
    } finally {
      setIsMerging(false);
    }
  }, [orgId, loadedPageCount, currentPage, logs, dispatch]);

  // Keep merged pool in sync when pages or current rows change.
  useEffect(() => {
    if (!orgId) return;
    void refreshMergedPool();
  }, [orgId, refreshMergedPool]);

  // Initial /metrics visit — load latest page when nothing is cached yet.
  useEffect(() => {
    if (!token || !permission || !orgId) return;
    if (initialFetchStarted.current) return;
    if (searchStatus === 'loading') return;
    if (logs?.length) return;
    initialFetchStarted.current = true;
    dispatch(setActivityLogsSearchStatus('loading'));
    void runActivityLogsSearch('latest', 1).finally(() => {
      initialFetchStarted.current = false;
    });
  }, [
    token,
    permission,
    orgId,
    logs?.length,
    searchStatus,
    dispatch,
    runActivityLogsSearch,
  ]);

  const fetchOlderLogsPage = useCallback(async (): Promise<boolean> => {
    if (!orgId || isFetchingPage) return false;
    const pageIsShort = (logs?.length || 0) < ACTIVITY_LOGS_PER_PAGE;
    if (pageIsShort || !hasMore) return false;
    const nextPage = loadedPageCount + 1;
    setIsFetchingPage(true);
    try {
      const res = await runActivityLogsSearch('search', nextPage, { silent: true });
      const body = res?.data && !Array.isArray(res.data)
        ? res.data as { logs?: typeof logs, hasMore?: boolean }
        : { logs: (res?.data || []) as typeof logs, hasMore: false };
      const rows = body.logs || [];
      if (res?.status !== 'success' || !rows.length) return false;
      persistSharedActivityLogsPage(orgId, nextPage, {
        logs: rows,
        hasMore: Boolean(body.hasMore),
      });
      dispatch(appendActivityLogsSearchLoadedPage(nextPage));
      dispatch(setActivityLogsSearchStatus('results'));
      await refreshMergedPool();
      return true;
    } finally {
      setIsFetchingPage(false);
    }
  }, [
    orgId,
    isFetchingPage,
    logs,
    hasMore,
    loadedPageCount,
    runActivityLogsSearch,
    dispatch,
    refreshMergedPool,
  ]);

  const isLoading = searchStatus === 'loading' || isFetchingPage || isMerging;
  const canFetchOlder = hasMore && (logs?.length || 0) >= ACTIVITY_LOGS_PER_PAGE;

  return {
    isLoading,
    isFetchingPage,
    canFetchOlder,
    fetchOlderLogsPage,
    refreshMergedPool,
  };
};
